import { Amplify, Auth, API } from "aws-amplify";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import isBetween from "dayjs/plugin/isBetween";
import duration from "dayjs/plugin/duration";
import advancedFormat from "dayjs/plugin/advancedFormat";
import CryptoJS from "crypto-js";
import mixpanel from "mixpanel-browser";
import relativeTime from "dayjs/plugin/relativeTime";
import isToday from "dayjs/plugin/isToday";

import {
  APIRoutes,
  amplifyConfigData,
  amplifyConfigDataNewSetUp,
  amplifyGoConfigData,
  amplifySSOConfigData,
  amplifyZpaxSSOConfigData,
  amplifyMicrosoftSSOConfigData,
  apiName,
  apiNameCDL,
  apiNameCDLDev,
} from "./config";

import { APIProviderFactory } from "./hexagon/adapters/driven/providers/apiProvider";
import { AuthServiceImplFactory } from "./hexagon/core/impl/authServiceImpl";
import { AuthProviderFactory } from "./hexagon/adapters/driven/providers/authProvider";
import { BillingRepositoryFactory } from "./hexagon/adapters/driven/repositories/billingRepository";
import { BillingServiceImplFactory } from "./hexagon/core/impl/billingServiceImpl";
import { BillingControllerFactory } from "./hexagon/adapters/driver/billingController";
import { ClinicRepositoryFactory } from "./hexagon/adapters/driven/repositories/clinicRepository";
import { ClinicServiceImplFactory } from "./hexagon/core/impl/clinicServiceImpl";
import { LocalStorageProviderFactory } from "./hexagon/adapters/driven/providers/localStorageProvider";
import { ClinicControllerFactory } from "./hexagon/adapters/driver/clinicController";
import { AlertRepositoryFactory } from "./hexagon/adapters/driven/repositories/alertRepository";
import { AlertControllerFactory } from "./hexagon/adapters/driver/alertController";
import { AlertServiceImplFactory } from "./hexagon/core/impl/alertServiceImpl";
import { ExternalStorageProviderFactory } from "./hexagon/adapters/driven/providers/externalStorageProvider";
import { AuthControllerFactory } from "./hexagon/adapters/driver/authController";
import { TimeProviderFactory } from "./hexagon/adapters/driven/providers/timeProvider";
import { AmplifyProviderFactory } from "./hexagon/adapters/driven/providers/amplifyProvider";
import { JSONUtilFactory } from "./hexagon/adapters/driven/providers/jsonUtilProvider";
import { CryptoProviderFactory } from "./hexagon/adapters/driven/providers/cryptoFactory";
import { CircadiaEngineProviderFactory } from "./hexagon/adapters/driven/providers/circadiaEngineProvider";
import { PsnAlertRepositoryFactory } from "./hexagon/adapters/driven/repositories/psnAlertRepository";
import { PsnAlertServiceImplFactory } from "./hexagon/core/impl/psnAlertServiceImpl";
import { PsnAlertControllerFactory } from "./hexagon/adapters/driver/psnAlertController";
import { SupportControllerFactory } from "./hexagon/adapters/driver/supportController";
import { SupportRepositoryFactory } from "./hexagon/adapters/driven/repositories/supportRepository";
import { SupportServiceImplFactory } from "./hexagon/core/impl/supportServiceImpl";
import { PushNotificationProviderFactory } from "./hexagon/adapters/driven/providers/pushNotificationProvider";
import { ReportRepositoryFactory } from "./hexagon/adapters/driven/repositories/reportRepository";
import { ReportServiceImplFactory } from "./hexagon/core/impl/reportServiceImpl";
import { ReportControllerFactory } from "./hexagon/adapters/driver/reportController";
import { PatientRepositoryFactory } from "./hexagon/adapters/driven/repositories/patientRepository";
import { PatientServiceImplFactory } from "./hexagon/core/impl/patientServiceImpl";
import { PatientControllerFactory } from "./hexagon/adapters/driver/patientController";
import { MedicationReconciliationRepositoryFactory } from "./hexagon/adapters/driven/repositories/medicationReconciliationRepository";
import { MedicationReconciliationServiceFactory } from "./hexagon/core/impl/medicationReconciliationServiceImpl";
import { MedicationReconciliationControllerFactory } from "./hexagon/adapters/driver/medicationReconciliationController";
import { NotificationRepositoryFactory } from "./hexagon/adapters/driven/repositories/notificationRepository";
import { NotificationServiceImplFactory } from "./hexagon/core/impl/notificationServiceImpl";
import { NotificationControllerFactory } from "./hexagon/adapters/driver/notificationController";
import { SearchControllerFactory } from "./hexagon/adapters/driver/searchController";
import { SearchServiceImplFactory } from "./hexagon/core/impl/searchServiceImpl";
import { SearchRepositoryFactory } from "./hexagon/adapters/driven/repositories/searchRepository";
import { WatchlistRepositoryFactory } from "./hexagon/adapters/driven/repositories/watchlistRepository";
import { watchlistServiceImplFactory } from "./hexagon/core/impl/watchlistServiceImpl";
import { WatchlistControllerFactory } from "./hexagon/adapters/driver/watchlistController";
import { RespRateRepositoryFactory } from "./hexagon/adapters/driven/repositories/respRateRepository";
import { RespRateServiceImplFactory } from "./hexagon/core/impl/respRateServiceImpl";
import { RespRateControllerFactory } from "./hexagon/adapters/driver/respRateController";
import { OfflineDeviceControllerFactory } from "./hexagon/adapters/driver/offlineDeviceController";
import { OfflineDeviceRepositoryFactory } from "./hexagon/adapters/driven/repositories/offlineDeviceRepository";
import { offlineDeviceServiceImplFactory } from "./hexagon/core/impl/offlineDeviceServiceImpl";
import { ConsentRepositoryFactory } from "./hexagon/adapters/driven/repositories/consentRepository";
import { consentServiceImplFactory } from "./hexagon/core/impl/consentServiceImpl";
import { ConsentControllerFactory } from "./hexagon/adapters/driver/consentController";
import { AnalyticsProviderFactory } from "./hexagon/adapters/driven/providers/AnalyticsProvider";
import { AnalyticsControllerFactory } from "./hexagon/adapters/driver/analyticsController";
import { CDLServiceImplFactory } from "./hexagon/core/impl/cdlServiceImpl";
import { CDLRepositoryFactory } from "./hexagon/adapters/driven/repositories/cdlRepository";
import { BedExitsControllerFactory } from "./hexagon/adapters/driver/bedExitsController";
import { BedExitsServiceImplFactory } from "./hexagon/core/impl/bedExitsServiceImpl";
import { BedExitsRepositoryFactory } from "./hexagon/adapters/driven/repositories/bedExitsRepository";
import { UserControllerFactory } from "./hexagon/adapters/driver/userController";
import { UserServiceImplFactory } from "./hexagon/core/impl/userServiceImpl";
import { UserRepositoryFactory } from "./hexagon/adapters/driven/repositories/userRepository";
import { webIntercomProviderFactory } from "./hexagon/adapters/driven/providers/webIntercomProvider";
import { Observable } from "./util/observable";
import { TrackingRepositoryFactory } from "./hexagon/adapters/driven/repositories/trackingRepository";
import { TrackingServiceImplFactory } from "./hexagon/core/impl/trackingServiceImpl";
import { TrackingControllerFactory } from "./hexagon/adapters/driver/trackingController";
import { SummaryRepositoryFactory } from "./hexagon/adapters/driven/repositories/summaryRepository";
import { SummaryServiceImplFactory } from "./hexagon/core/impl/summaryServiceImpl";
import { SummaryControllerFactory } from "./hexagon/adapters/driver/summaryController";
import { ElectronEngineProviderFactory } from "./hexagon/adapters/driven/providers/electronEngineProvider";
import { InsightsRepositoryFactory } from "./hexagon/adapters/driven/repositories/insightsRepository";
import { insightsServiceImplFactory } from "./hexagon/core/impl/insightsServiceImpl";
import { InsightsControllerFactory } from "./hexagon/adapters/driver/insightsController";
import { IPVRepositoryFactory } from "./hexagon/adapters/driven/repositories/ipvRepository";
import { ipvServiceImplFactory } from "./hexagon/core/impl/ipvServiceImpl";
import { IPVControllerFactory } from "./hexagon/adapters/driver/ipvController";
import { HomeHealthControllerFactory } from "./hexagon/adapters/driver/homeHealthController";
import { HomeHealthServiceImplFactory } from "./hexagon/core/impl/homeHealthServiceImpl";
import { HomeHealthRepositoryFactory } from "./hexagon/adapters/driven/repositories/homeHealthRepository";
import { SalesRepositoryFactory } from "./hexagon/adapters/driven/repositories/salesRepository";
import { SalesServiceImplFactory } from "./hexagon/core/impl/salesServiceImpl";
import { SalesControllerFactory } from "./hexagon/adapters/driver/salesController";
import { RTBloggerRepositoryFactory } from "./hexagon/adapters/driven/repositories/rtBloggerRepository";
import { rtBloggerServiceImplFactory } from "./hexagon/core/impl/rtBloggerServiceImpl";
import { RTBloggerControllerFactory } from "./hexagon/adapters/driver/rtBloggerController";
import { VitalSyncRepositoryFactory } from "./hexagon/adapters/driven/repositories/vitalSyncRepository";
import { VitalSyncServiceImplFactory } from "./hexagon/core/impl/vitalSyncServiceImpl";
import { VitalSyncControllerFactory } from "./hexagon/adapters/driver/vitalSyncController";
import { SMSRepositoryFactory } from "./hexagon/adapters/driven/repositories/smsRepository";
import { SMSServiceImplFactory } from "./hexagon/core/impl/smsServiceImpl";
import { SMSControllerFactory } from "./hexagon/adapters/driver/smsController";
import { AppPerformanceSurveyRepositoryFactory } from "./hexagon/adapters/driven/repositories/appPerformanceSurveyRepository";
import { AppPerformanceSurveyServiceImplFactory } from "./hexagon/core/impl/appPerformanceSurveyServiceImpl";
import { AppPerformanceSurveyControllerFactory } from "./hexagon/adapters/driver/appPerformanceSurveyController";
import { FacilityRepositoryFactory } from "./hexagon/adapters/driven/repositories/facilityRepository";
import { FacilityServiceImplFactory } from "./hexagon/core/impl/facilityServiceImpl";
import { FacilityControllerFactory } from "./hexagon/adapters/driver/facilityController";

import { HealthScribeWebSocketProvider } from "./hexagon/adapters/driven/providers/healthScribeWebSocketProvider";
import { HealthScribeAudioProvider } from "./hexagon/adapters/driven/providers/healthScribeAudioProvider";
import { HealthScribeRepositoryFactory } from "./hexagon/adapters/driven/repositories/healthScribeRepository";
import { HealthScribeServiceFactory } from "./hexagon/core/impl/healthScribeServiceImpl";
import { HealthScribeControllerFactory } from "./hexagon/adapters/driver/healthScribeController";

import { NavigatorRepositoryFactory } from "./hexagon/adapters/driven/repositories/navigatorRepository";
import { NavigatorServiceImplFactory } from "./hexagon/core/impl/navigatorServiceImpl";
import { NavigatorControllerFactory } from "./hexagon/adapters/driver/navigatorController";

import { HPITemplateRepositoryFactory } from "./hexagon/adapters/driven/repositories/hpiTemplateRepository";
import { HPITemplateServiceImplFactory } from "./hexagon/core/impl/hpiTemplateServiceImpl";
import { HPITemplateControllerFactory } from "./hexagon/adapters/driver/hpiTemplateController";

import { EngagementTrackerProviderFactory } from "./hexagon/adapters/driven/providers/engagementTrackerProvider";

import { VisitNotesRepositoryFactory } from "./hexagon/adapters/driven/repositories/VisitNotesRepository";
import { VisitNotesServiceImplFactory } from "./hexagon/core/impl/VisitNotesServiceImpl";
import { VisitNotesControllerFactory } from "./hexagon/adapters/driver/VisitNotesController";

export const amplifyProvider = AmplifyProviderFactory({
  amplify: Amplify,
  auth: Auth,
  amplifyConfig: {
    amplifyConfigData,
    amplifyConfigDataNewSetUp,
    amplifyGoConfigData,
    amplifySSOConfigData,
    amplifyZpaxSSOConfigData,
    amplifyMicrosoftSSOConfigData,
  },
});
amplifyProvider.amplifyConfigure();

export const cryptoJsProvider = (secretKey: string) =>
  CryptoProviderFactory({
    cryptoJS: CryptoJS,
    secretKey: secretKey,
  });

export const circadiaEngineProvider = CircadiaEngineProviderFactory({
  circadia: window.circadia,
});

export const electronEngineProvider = ElectronEngineProviderFactory({
  electron: window.electron,
});

export const webIntercomProvider = webIntercomProviderFactory({
  globWindow: window,
});

export const analyticsProvider = AnalyticsProviderFactory(mixpanel);

export const analyticsController = AnalyticsControllerFactory({
  analyticsProvider: analyticsProvider,
  circadiaEngineProvider: circadiaEngineProvider,
});

export const timeProvider = TimeProviderFactory({
  dateLibrary: dayjs,
  utcPlugin: utc,
  timezonePlugin: timezone,
  isBetween,
  advancedFormat,
  relativeTime,
  duration,
  isToday,
});

export const AuthObserver = new Observable({ type: "initial", data: null });
if (
  circadiaEngineProvider.isWindowCircadia() &&
  circadiaEngineProvider.intfv >= 5
  // && circadiaEngineProvider.isIOS
) {
  if (circadiaEngineProvider.setCognitoLoginDoneCb)
    circadiaEngineProvider.setCognitoLoginDoneCb(() => {
      AuthObserver.update({ type: "login", payload: { success: true } });
    });

  if (circadiaEngineProvider.setCognitoLoginFailedCb)
    circadiaEngineProvider.setCognitoLoginFailedCb((error) => {
      AuthObserver.update({
        type: "login",
        payload: { success: false, error: error?.Msg },
      });
    });

  if (circadiaEngineProvider.setCognitoMfaRequiredCb)
    circadiaEngineProvider.setCognitoMfaRequiredCb((props) => {
      AuthObserver.update({
        type: "mfaRequired",
        payload: { success: false, props: props?.CODE_DELIVERY_DESTINATION },
      });
    });

  if (circadiaEngineProvider.setCognitoPinRequiredCb)
    circadiaEngineProvider.setCognitoPinRequiredCb(() => {
      AuthObserver.update({ type: "pinRequired", payload: null });
    });

  if (circadiaEngineProvider.setCognitoNewPasswordRequiredCb)
    circadiaEngineProvider.setCognitoNewPasswordRequiredCb((attrs) => {
      AuthObserver.update({ type: "newPasswordRequired", payload: attrs });
    });

  if (circadiaEngineProvider.setCognitoEmailOTPRequiredCb)
    circadiaEngineProvider.setCognitoEmailOTPRequiredCb(() => {
      AuthObserver.update({ type: "passwordlessOTPRequired", payload: null });
    });
}

const authProvider = AuthProviderFactory({
  Auth: amplifyProvider.auth,
  CircadiaEngine: circadiaEngineProvider,
  ElectronEngine: electronEngineProvider,
});

export const AuthService = AuthServiceImplFactory({
  authProvider,
  circadiaEngineProvider,
  electronEngineProvider,
});

export const APIProvider = APIProviderFactory({
  API,
  AuthService,
  apiName,
});

export const CDLAPIProvider = APIProviderFactory({
  API,
  AuthService,
  apiName: apiNameCDL,
});

export const CDLDevAPIProvider = APIProviderFactory({
  API,
  AuthService,
  apiName: apiNameCDLDev,
});

// Billing
const billingRepository = BillingRepositoryFactory({
  APIRoutes,
  APIProvider,
});

const billingService = BillingServiceImplFactory({ billingRepository });

export const billingController = BillingControllerFactory({
  billingService,
});
// End Billing

export const jsonUtil = JSONUtilFactory({ JSON });
export const localStorageProvider = LocalStorageProviderFactory({
  localStorage: localStorage,
});

export const engagementTrackerProvider = EngagementTrackerProviderFactory({
  localStorageProvider,
});

export const pushNotificationProvider = PushNotificationProviderFactory({
  circadiaEngineProvider,
  APIProvider,
  apiRoute: APIRoutes.apiStoreToken,
  AuthService,
});

export const externalStorageProvider = ExternalStorageProviderFactory({
  APIProvider,
  APIPath: APIRoutes.apiGetFileFromS3,
  AuthService,
});

// Notification
const notificationRepository = NotificationRepositoryFactory({
  APIProvider,
  AuthService,
  APIRoutes,
});

const notificationService = NotificationServiceImplFactory({
  notificationRepository,
});

export const notificationController = NotificationControllerFactory({
  notificationService,
});

// Clinic
const clinicRepository = ClinicRepositoryFactory({
  APIProvider,
  APIRoutes,
  AuthService,
});

const clinicService = ClinicServiceImplFactory({
  clinicRepository,
  localStorageProvider,
  notificationService,
});
//

// Alert
const alertRepository = AlertRepositoryFactory({
  APIProvider,
  AuthService,
  APIRoutes,
});

const alertService = AlertServiceImplFactory({
  alertRepository,
  externalStorageProvider,
});
//

// PSN Alert
const psnAlertRepository = PsnAlertRepositoryFactory({
  APIProvider,
  AuthService,
  APIRoutes,
});

const psnAlertService = PsnAlertServiceImplFactory({
  psnAlertRepository,
});
//

// Patient
const patientRepository = PatientRepositoryFactory({
  APIProvider,
  AuthService,
  APIRoutes,
});

const patientService = PatientServiceImplFactory({
  patientRepository,
});

//Report
const reportRepository = ReportRepositoryFactory({
  APIProvider,
  AuthService,
  APIRoutes,
});

const reportService = ReportServiceImplFactory({
  reportRepository,
});

export const cdlRepository = CDLRepositoryFactory({
  APIProvider: CDLAPIProvider,
  APIProvider2: CDLDevAPIProvider,
  AuthService,
  APIRoutes,
  timeProvider,
});

const cdlService = CDLServiceImplFactory({ cdlRepository });

export const patientController = PatientControllerFactory({
  patientService,
  timeProvider,
  cdlService,
});

// MedicationReconciliation
const medicationReconciliationRepository =
  MedicationReconciliationRepositoryFactory({
    APIProvider,
    AuthService,
    APIRoutes,
  });
const medicationReconciliationService = MedicationReconciliationServiceFactory({
  medicationReconciliationRepository,
});
export const medicationReconciliationController =
  MedicationReconciliationControllerFactory({
    medicationReconciliationService,
  });
//

// Resprate
const respRateRepository = RespRateRepositoryFactory({
  APIProvider,
  APIRoutes,
  AuthService,
});

const respRateService = RespRateServiceImplFactory({
  respRateRepository,
});

export const respRateController = RespRateControllerFactory({
  respRateService,
});
//

// Watchlist
const watchlistRepository = WatchlistRepositoryFactory({
  APIProvider,
  APIRoutes,
  AuthService,
});

const watchlistService = watchlistServiceImplFactory({ watchlistRepository });

export const watchlistController = WatchlistControllerFactory({
  watchlistService,
});

// User
const userRepository = UserRepositoryFactory({
  APIProvider,
  APIRoutes,
  AuthService,
});
const userService = UserServiceImplFactory({ userRepository });

export const userController = UserControllerFactory({
  userService,
});

// BedExits
const bedExitsRepository = BedExitsRepositoryFactory({
  APIProvider,
  APIRoutes,
  AuthService,
});
const bedExitsService = BedExitsServiceImplFactory({ bedExitsRepository });

export const bedExitsController = BedExitsControllerFactory({
  bedExitsService,
});

// Offline device
const offlineDeviceRepository = OfflineDeviceRepositoryFactory({
  APIProvider,
  APIRoutes,
  AuthService,
});

const offlineDeviceService = offlineDeviceServiceImplFactory({
  offlineDeviceRepository,
});

export const offlineDeviceController = OfflineDeviceControllerFactory({
  offlineDeviceService,
});

// Insights
const insightsRepository = InsightsRepositoryFactory({
  APIProvider,
  APIRoutes,
  AuthService,
});

const insightsService = insightsServiceImplFactory({
  insightsRepository,
});

export const insightsController = InsightsControllerFactory({
  insightsService,
  timeProvider,
});

//ipv
const ipvRepository = IPVRepositoryFactory({
  APIProvider,
  APIRoutes,
  AuthService,
});

const ipvService = ipvServiceImplFactory({ ipvRepository });

export const ipvController = IPVControllerFactory({
  ipvService,
});

//consent
const consentRepository = ConsentRepositoryFactory({
  APIProvider,
  APIRoutes,
  AuthService,
});

const consentService = consentServiceImplFactory({ consentRepository });

export const consentController = ConsentControllerFactory({
  consentService,
});
//

export const reportController = ReportControllerFactory({
  reportService,
  timeProvider,
});
export const clinicController = ClinicControllerFactory({
  clinicService,
});
export const alertController = AlertControllerFactory({
  alertService,
  externalStorageProvider,
});
export const psnAlertController = PsnAlertControllerFactory({
  psnAlertService,
});

export const authController = AuthControllerFactory({
  AuthService,
  localStorageProvider,
  cryptoJsProvider,
  AuthObserver,
  circadiaEngineProvider,
  electronEngineProvider,
});

// Z-Pax

const supportRepository = SupportRepositoryFactory({
  APIProvider,
  APIRoutes,
  AuthService,
});

const supportService = SupportServiceImplFactory({
  supportRepository,
});

export const supportController = SupportControllerFactory({
  supportService,
});

const searchRepository = SearchRepositoryFactory({
  APIProvider,
  AuthService,
  APIRoutes,
});

const searchService = SearchServiceImplFactory({
  searchRepository,
});
export const searchController = SearchControllerFactory({
  searchService,
});

const trackingRepository = TrackingRepositoryFactory({
  APIProvider,
  APIRoutes,
  AuthService,
});

const trackingService = TrackingServiceImplFactory({
  trackingRepository,
});

export const trackingController = TrackingControllerFactory({
  trackingService,
});

const summaryRepository = SummaryRepositoryFactory({
  APIProvider,
  APIRoutes,
  AuthService,
});

const summaryService = SummaryServiceImplFactory({
  summaryRepository,
});

export const summaryController = SummaryControllerFactory({
  summaryService,
});

// Home Health
const homeHealthRepository = HomeHealthRepositoryFactory({
  APIProvider,
  APIRoutes,
  AuthService,
});

const homeHealthService = HomeHealthServiceImplFactory({
  homeHealthRepository,
});

export const homeHealthController = HomeHealthControllerFactory({
  homeHealthService,
});

//sales
const salesRepository = SalesRepositoryFactory({
  APIProvider,
  APIRoutes,
  AuthService,
});

const salesService = SalesServiceImplFactory({ salesRepository });

export const salesController = SalesControllerFactory({
  salesService,
});

//rtblogger
const rtBloggerRepository = RTBloggerRepositoryFactory({
  APIProvider,
  APIRoutes,
  AuthService,
});

const rtBloggerService = rtBloggerServiceImplFactory({ rtBloggerRepository });

export const rtBloggerController = RTBloggerControllerFactory({
  rtBloggerService,
});

// VitalSync
const vitalSyncRepository = VitalSyncRepositoryFactory({
  APIProvider,
  APIRoutes,
  AuthService,
});

const vitalSyncService = VitalSyncServiceImplFactory({ vitalSyncRepository });

export const vitalSyncController = VitalSyncControllerFactory({
  vitalSyncService,
});

// SMS
const smsRepository = SMSRepositoryFactory({
  APIProvider,
  APIRoutes,
  AuthService,
});

const smsService = SMSServiceImplFactory({ smsRepository });

export const smsController = SMSControllerFactory({
  smsService,
});

// Initial Patient Visit
const navigatorRepository = NavigatorRepositoryFactory({
  APIProvider,
  AuthService,
  APIRoutes,
});

const navigatorService = NavigatorServiceImplFactory({
  navigatorRepository,
});

export const navigatorController = NavigatorControllerFactory({
  navigatorService,
});

// AppPerformance Survey
const appPerformanceSurveyRepository = AppPerformanceSurveyRepositoryFactory({
  APIProvider,
  AuthService,
  APIRoutes,
});

const appPerformanceSurveyService = AppPerformanceSurveyServiceImplFactory({
  appPerformanceSurveyRepository,
});

export const appPerformanceSurveyController =
  AppPerformanceSurveyControllerFactory({
    appPerformanceSurveyService,
  });

const facilityRepository = FacilityRepositoryFactory({
  APIProvider: APIProvider,
  AuthService: AuthService,
  APIRoutes,
});

const facilityService = FacilityServiceImplFactory({
  facilityRepository,
});

export const facilityController = FacilityControllerFactory({
  facilityService,
});

// HealthScribe Components
export const healthScribeWebSocketProvider =
  new HealthScribeWebSocketProvider();
export const healthScribeAudioProvider = new HealthScribeAudioProvider();

const healthScribeRepository = HealthScribeRepositoryFactory({
  apiProvider: APIProvider,
  authService: authController,
});

const healthScribeService = HealthScribeServiceFactory({
  healthScribeRepository,
  healthScribeWebSocketProvider,
  healthScribeAudioProvider,
});

export const healthScribeController = HealthScribeControllerFactory({
  healthScribeService,
  healthScribeWebSocketProvider,
  healthScribeAudioProvider,
  analyticsController: analyticsController,
});

// HPI Template
const hpiTemplateRepository = HPITemplateRepositoryFactory({
  APIProvider,
  AuthService,
  APIRoutes,
});

const hpiTemplateService = HPITemplateServiceImplFactory({
  hpiTemplateRepository,
});

export const hpiTemplateController = HPITemplateControllerFactory({
  hpiTemplateService,
});

// Visit Notes (Unified API for NP Visit and At Risk Review)
const visitNotesRepository = VisitNotesRepositoryFactory({
  APIProvider,
  AuthService,
  APIRoutes,
});

const visitNotesService = VisitNotesServiceImplFactory({
  visitNotesRepository,
});

export const visitNotesController = VisitNotesControllerFactory({
  visitNotesService,
});

// Microsoft Graph API utilities (for testing/debugging)
// Exposes window.microsoftGraph for browser console testing
import "./util/microsoftGraphApi";

// MSAL Provider for Microsoft Graph API authentication
// Exposes window.msal for browser console testing
import "./hexagon/adapters/driven/providers/msalProvider";
